/**
 * Connection status states
 */
export type ConnectionStatus =
  | 'disconnected'
  | 'disconnecting'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

/**
 * WebSocket-like interface for cross-platform support
 */
export interface WebSocketLike {
  readonly readyState: number;
  readonly CONNECTING: number;
  readonly OPEN: number;
  readonly CLOSING: number;
  readonly CLOSED: number;

  onopen: ((event: Event | { type: string }) => void) | null;
  onclose: ((event: CloseEvent | { code: number; reason: string }) => void) | null;
  onmessage: ((event: MessageEvent | { data: string }) => void) | null;
  onerror: ((event: Event | Error) => void) | null;

  send(data: string | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
}

/**
 * Factory function for creating WebSocket instances
 */
export type WebSocketFactory = (url: string) => WebSocket | WebSocketLike;

/**
 * Connection configuration
 */
export interface ConnectionConfig {
  /** WebSocket URL (default: ws://127.0.0.1:4500/bridge) */
  url: string;

  /** Connection timeout in milliseconds (default: 10000) */
  connectTimeoutMs: number;

  /** Enable auto-reconnect on disconnect (default: true) */
  autoReconnect: boolean;

  /** Maximum reconnection attempts, 0 = infinite (default: 0) */
  maxReconnectAttempts: number;

  /** Base delay between reconnects in ms (default: 1000) */
  reconnectBaseDelayMs: number;

  /** Maximum reconnect delay in ms (default: 30000) */
  reconnectMaxDelayMs: number;

  /** Backoff multiplier for reconnect delay (default: 2) */
  reconnectBackoffMultiplier: number;

  /** Enable heartbeat ping/pong (default: true) */
  heartbeatEnabled: boolean;

  /** Heartbeat interval in ms (default: 30000) */
  heartbeatIntervalMs: number;

  /** Heartbeat timeout in ms (default: 5000) */
  heartbeatTimeoutMs: number;

  /** Custom WebSocket factory (for Node.js ws package) */
  websocketFactory?: WebSocketFactory;
}

/**
 * Default connection configuration
 */
export const DEFAULT_CONNECTION_CONFIG: ConnectionConfig = {
  url: 'ws://127.0.0.1:4500/bridge',
  connectTimeoutMs: 10000,
  autoReconnect: true,
  maxReconnectAttempts: 0,
  reconnectBaseDelayMs: 1000,
  reconnectMaxDelayMs: 30000,
  reconnectBackoffMultiplier: 2,
  heartbeatEnabled: true,
  heartbeatIntervalMs: 30000,
  heartbeatTimeoutMs: 5000,
};
